import axios from "axios";
import { toInteger } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectLevel } from "store/game/selectors";
import { init, setCodes, setItems } from "store/game";
import { createPartQueries, prepareItems } from "helpers/partition-helpers";
import { preloadImages } from "helpers/image-helpers";
import { checkIfPartsAreValid } from "pages/home/Levels";

function mapToCodeDesc(item) {
  return {
    code: item?.attributes?.code,
    description: item?.attributes?.description,
  };
}

function convertArray(arr) {
  let map = {};
  let seen = new Set();
  for (let item of arr) {
    if (!seen.has(item.code)) {
      map[item.code] = { description: item.description, children: {} };
      seen.add(item.code);
    }
  }
  let result = {};
  for (let item of arr) {
    if (item.code.length === 2) {
      result[item.code] = map[item.code];
    } else if (item.code.length === 4) {
      let parentCode = item.code.substring(0, 2);
      if (!map[parentCode]) {
        map[parentCode] = { description: "", children: {} };
        result[parentCode] = map[parentCode];
      }
      if (!map[parentCode].children[item.code] && parentCode !== item.code) {
        map[parentCode].children[item.code] = map[item.code];
      }
    } else if (item.code.length > 4) {
      let parentCode = item.code.substring(0, 4);
      if (!map[parentCode]) {
        map[parentCode] = { description: "", children: {} };
        if (map[parentCode.substring(0, 2)]) {
          map[parentCode.substring(0, 2)].children[parentCode] =
            map[parentCode];
        }
      }
      if (!map[parentCode].children[item.code] && parentCode !== item.code) {
        map[parentCode].children[item.code] = map[item.code];
      }
    }
  }
  return result;
}

export default function useLoader({ onLoad, disabled = false }) {
  const dispatch = useDispatch();
  const selectedLevel = useSelector(selectLevel);

  // Loading
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Progress
  const [imagesProgress, setImagesProgress] = useState(0);

  // Error
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    if (selectedLevel && !disabled) {
      const selectedParts = selectedLevel?.parts;
      const isPartitionsValid = checkIfPartsAreValid(selectedParts);

      if (!isPartitionsValid) {
        setError(
          "Məhsullar seçilməyib, zəhmət olmasa idarə panelindən məhsullar seçin."
        );
        return;
      }

      const queries = createPartQueries(selectedParts);

      axios
        .all(queries)
        .then((res) => {
          const flatPartitions = res.flat();
          const codesGrid = convertArray(flatPartitions.map(mapToCodeDesc));

          dispatch(setCodes(codesGrid));

          if (flatPartitions.length === 0) {
            setError(
              "Məhsullar tapılmadı, zəhmət olmasa idarə panelindən məhsullar əlavə edin."
            );

            return;
          }
          const items = prepareItems(flatPartitions);

          if (items.length === 0) {
            setError(
              "Məhsullar üçün şəkillər tapılmadı, zəhmət olmasa idarə panelindən şəkillər əlavə edin."
            );
            return;
          }

          const flatImages = items.map(function getImageFromItem(item) {
            return item?.imagePath;
          });

          dispatch(setItems(items));

          preloadImages(flatImages, {
            setProgress: setImagesProgress,
            signal: controller.signal,
          })
            .then(() => {
              dispatch(init());
              onLoad();
              setIsLoadingImages(false);
            })
            .catch((error) => {
              setIsLoadingImages(false);
              setError(
                "Şəkillər yüklənərkən xəta baş verdi, zəhmət olmasa biraz sonra yenidən cəhd edin."
              );
            });

          setIsLoading(false);
          setIsLoadingImages(true);
        })
        .catch((error) => {
          setError(error.message);
        });

      setIsLoading(true);
    }

    return () => {
      // Stop loading images if the component is unmounted
      controller.abort();
    };
  }, [dispatch, selectedLevel, disabled]);

  useEffect(() => {
    if (error) {
      setIsLoading(false);
      setIsLoadingImages(false);
    }
  }, [error]);

  return {
    isLoading,
    isLoadingImages,
    error,
    setError,
    imagesProgress: toInteger(imagesProgress),
  };
}
